import { useCallback, useEffect, useMemo, useState } from 'react';
import { type AgentPolicy } from '@repo/api/schemas/agentPolicy.schema';
import { TableSkeletonLoader } from '@repo/ui';
import {
  Box,
  ButtonIcon,
  Chip,
  Icon,
  Loader,
  TableSortable,
  Typography,
} from '@saviynt/design-system';
import { useNavigate } from '@tanstack/react-router';
import { useIntl } from 'react-intl';

import type { PolicyFilterValues } from '../PolicyPageFilter/PolicyPageFilter';

import { useAlertBanner } from '../../../../shared/app/contexts/AlertBannerContext';
import { ALERT_BANNER_TYPES, TOKENS } from '../../../../shared/constants';
import useDebounce from '../../../../shared/hooks/useDebounce';
import { useAgentPolicyList } from '../../hooks/useAgentPolicy';
import { formatRuleSummary } from '../../utils/ruleSummary';
import ActionButton from '../ActionButton/ActionButton';
import { TextCell } from '../CellRenderer/CellRenderer';
import NoFilteredPolicyState from '../NoFilteredPolicyState/NoFilteredPolicyState';
import NoPolicyState from '../NoPolicyState/NoPolicyState';

import { getSortConfigs, policyTableColumns } from './PolicyTableColumnConfig';

import './PolicyPageTable.css';

const POLICY_COLUMNS_SESSION_KEY = 'agent:policy:columns:v2';
const POLICY_SORT_SESSION_KEY = 'agent:policy:sort';

type StoredSort = { field: string; direction: string } | null;

const getStoredPolicyColumns = () => {
  try {
    const stored = sessionStorage.getItem(POLICY_COLUMNS_SESSION_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore corrupted or unavailable session storage
  }
  return policyTableColumns;
};

const getStoredPolicySort = (): StoredSort => {
  try {
    const stored = sessionStorage.getItem(POLICY_SORT_SESSION_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore corrupted or unavailable session storage
  }
  return null;
};

export const StatusChip = ({ isActive }: { isActive: boolean }) => {
  const intl = useIntl();
  const label = isActive
    ? intl.formatMessage({
        id: 'WiltoA',
        defaultMessage: 'Active',
        description: 'Policy active status',
      })
    : intl.formatMessage({
        id: 'IXZxoP',
        defaultMessage: 'Inactive',
        description: 'Policy inactive status',
      });
  return (
    <span data-sort-value={label}>
      <Chip
        label={label}
        color={isActive ? 'success' : 'neutral'}
        kind='subtleFilled'
        isInteractionDisabled
      />
    </span>
  );
};

export interface PolicyPageTableProps {
  filterValues: PolicyFilterValues;
  selectedPolicy: AgentPolicy | null;
  onRowClick: (policy: AgentPolicy) => void;
  onPolicyUpdate: (data: Partial<AgentPolicy>) => void;
  setIsSidePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function PolicyPageTable({
  filterValues,
  selectedPolicy,
  onRowClick,
  onPolicyUpdate,
  setIsSidePanelOpen,
}: PolicyPageTableProps) {
  const intl = useIntl();
  const navigate = useNavigate();

  const debouncedFilterValues = useDebounce(filterValues, 500);

  const [columns, setColumnsState] = useState(getStoredPolicyColumns);
  const [currentSort, setCurrentSort] =
    useState<StoredSort>(getStoredPolicySort);
  const { show: showAlertBanner } = useAlertBanner();

  const showPolicySuccessBanner = useCallback(
    (title: string) => {
      showAlertBanner({
        colorTheme: ALERT_BANNER_TYPES.SUCCESS,
        title,
        autoDismissDelay: 4000,
        isFloating: false,
        isColonVisible: false,
      });
    },
    [showAlertBanner]
  );

  const setColumns = useCallback((newColumns: typeof policyTableColumns) => {
    setColumnsState(newColumns);
    try {
      sessionStorage.setItem(
        POLICY_COLUMNS_SESSION_KEY,
        JSON.stringify(newColumns)
      );
    } catch {
      // Ignore session storage write failures
    }
  }, []);

  const handleSort = useCallback((field: string, direction: string) => {
    const newSort = { field, direction };
    setCurrentSort(newSort);
    try {
      sessionStorage.setItem(POLICY_SORT_SESSION_KEY, JSON.stringify(newSort));
    } catch {
      // Ignore session storage write failures
    }
  }, []);

  const sortConfigs = useMemo(
    () =>
      getSortConfigs(intl).map((config) => ({
        ...config,
        sortOptions: config.sortOptions.map((opt) => ({
          ...opt,
          isChecked:
            currentSort?.field === config.key &&
            currentSort?.direction === opt.value,
        })),
      })),
    [intl, currentSort?.field, currentSort?.direction]
  );

  useEffect(() => {
    const message = sessionStorage.getItem('policySuccessBanner');
    if (message) {
      showPolicySuccessBanner(message);
      sessionStorage.removeItem('policySuccessBanner');
    }
  }, [showPolicySuccessBanner]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useAgentPolicyList(debouncedFilterValues);

  const handleTableScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < 200) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const tableData = useMemo(() => {
    return data?.pages.flatMap((page) => page.policyList) || [];
  }, [data]);

  const sortedTableData = useMemo(() => {
    if (!currentSort) {
      return tableData;
    }
    const { field, direction } = currentSort;
    return [...tableData].sort((a, b) => {
      let aVal: string;
      let bVal: string;
      if (field === 'status') {
        aVal = a.isActive ? 'Active' : 'Inactive';
        bVal = b.isActive ? 'Active' : 'Inactive';
      } else if (field === 'ruleSummary') {
        aVal = formatRuleSummary(a, intl);
        bVal = formatRuleSummary(b, intl);
      } else {
        aVal = String((a as Record<string, unknown>)[field] ?? '');
        bVal = String((b as Record<string, unknown>)[field] ?? '');
      }
      const cmp = aVal.localeCompare(bVal);
      return direction === 'asc' ? cmp : -cmp;
    });
  }, [tableData, currentSort, intl]);

  const tableRows = sortedTableData.map((policy) => ({
    id: policy.id,
    name: (
      <div className='PolicyPageTable-nameCell'>
        <Box className='PolicyPageTable-nameIconBox'>
          <Icon kind='Policy' color={TOKENS.CF_NEU_BLD} />
        </Box>
        <Box>
          <Typography
            kind='h3'
            desktop='h3-d'
            mobile='h3-d'
            color={TOKENS.CF_SEC_BLD}>
            {policy.name}
          </Typography>
        </Box>
      </div>
    ),
    description: <TextCell text={policy.description} lineLimit={2} />,
    ruleSummary: (
      <TextCell text={formatRuleSummary(policy, intl)} lineLimit={2} />
    ),
    status: <StatusChip isActive={policy.isActive} />,
    actionColumn: (
      <div className='PolicyPageTable-actionCell'>
        <ButtonIcon
          size='medium'
          kind='ghost'
          icon={<Icon kind='Edit' size='small' />}
          ariaLabel={intl.formatMessage({
            id: 'fbmJJE',
            defaultMessage: 'Edit rule',
            description: 'Edit policy button',
          })}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            navigate({
              to: '/policies/edit/$policyId',
              params: { policyId: policy.id },
            });
          }}
        />
        <ActionButton
          rowData={policy}
          onPolicyUpdate={onPolicyUpdate}
          onStatusChange={showPolicySuccessBanner}
          onPolicyDeleted={(id) => {
            if (selectedPolicy?.id === id) {
              setIsSidePanelOpen(false);
            }
            showPolicySuccessBanner(
              intl.formatMessage({
                id: 'UVZ+4Z',
                defaultMessage: 'Rule deleted successfully.',
                description: 'Success banner message on policy deletion',
              })
            );
          }}
        />
      </div>
    ),
  }));

  const selectedRowIndex = useMemo(() => {
    if (!selectedPolicy) {
      return undefined;
    }
    const i = sortedTableData.findIndex((p) => p.id === selectedPolicy.id);
    return i >= 0 ? i : undefined;
  }, [selectedPolicy, sortedTableData]);

  const handleRowClick = useCallback(
    (rowOrIndex: unknown, maybeIndex: unknown) => {
      let index: number | undefined;
      if (typeof maybeIndex === 'number' && maybeIndex >= 0) {
        index = maybeIndex;
      } else if (typeof rowOrIndex === 'number' && rowOrIndex >= 0) {
        index = rowOrIndex;
      } else if (
        rowOrIndex &&
        typeof rowOrIndex === 'object' &&
        'id' in rowOrIndex &&
        typeof (rowOrIndex as { id: string }).id === 'string'
      ) {
        index = sortedTableData.findIndex(
          (p) => p.id === (rowOrIndex as { id: string }).id
        );
      }
      if (index === undefined || index < 0) {
        return;
      }
      const policy = sortedTableData[index];
      if (policy) {
        onRowClick(policy);
      }
    },
    [sortedTableData, onRowClick]
  );

  if (isLoading) {
    return (
      <Box className='PolicyPageTable-loaderRow'>
        <TableSkeletonLoader />
      </Box>
    );
  }

  const isFilterApplied =
    Boolean(filterValues.searchValue) ||
    Object.values(filterValues.selectedFilters).some(
      (v) => Array.isArray(v) && v.length > 0
    );

  if (sortedTableData.length === 0) {
    return (
      <Box className='PolicyPageTable-emptyStateRow'>
        {isFilterApplied ? <NoFilteredPolicyState /> : <NoPolicyState />}
      </Box>
    );
  }

  return (
    <div className='PolicyPageTable-tableWrapper'>
      <TableSortable
        columnConfigs={{ isDndDisabled: true }}
        columns={columns}
        setColumns={setColumns}
        data={tableRows}
        sortConfigs={sortConfigs}
        onSort={handleSort}
        onScroll={handleTableScroll}
        selectedRowIndex={selectedRowIndex}
        onRowClick={handleRowClick as () => void}
        isVirtualizationEnabled={false}
        isWindowScroll={false}
        tableHeight='min(72vh, calc(100dvh - 14rem))'
        className='PolicyPageTable-table'
        dataTestId='PolicyPageTable-Table'
      />
      {isFetchingNextPage && (
        <div
          className='PolicyPageTable-nextPageLoader'
          data-testid='loader-loop'>
          <Loader kind='loop' format='main' color='brand' />
        </div>
      )}
    </div>
  );
}
